'use client';

import './account-settings-section.scss';

import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@sola/ark-core/molecules/tooltip/tooltip.tsx';
import { SupporterIdentity } from '@sola/ark-skippy/molecules/supporter-identity/supporter-identity.tsx';
import type {
  GraphQLSupporterAddress,
  GraphQLSupporterContact,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { SENSITIVE_MASK_CHAR } from '@sola/shared-utils/constants.ts';
import { useReadyForQueries } from '@sola/supporter-sdk/hooks/base.ts';
import type { SupporterDetailsData } from '@sola/supporter-sdk/queries.ts';
import {
  getSupporterFullName,
  getSupporterInitials,
} from '@sola/supporter-sdk/utils/name-format.ts';

import { SettingsModalContact } from '../../organisms/settings-modal-contact/settings-modal-contact.tsx';
import { SettingsModalMailing } from '../../organisms/settings-modal-mailing/settings-modal-mailing.tsx';
import { SettingsModalPassword } from '../../organisms/settings-modal-password/settings-modal-password.tsx';
import { SettingsModalUsername } from '../../organisms/settings-modal-username/settings-modal-username.tsx';
import { LocalAlertsProvider } from '../local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../local-alerts/local-alerts.tsx';

type AccountSettingsSectionProps = {
  title: string;
  children: React.ReactNode;
};

// The supporter is effectively the primary contact for their own account,
// so we shape their details as a `GraphQLSupporterContact` to feed the
// shared `SettingsModalContact`. `id` is the Salesforce primary contact
// id (from auth), not the supporter globalId — the update mutation uses
// it to route the PATCH to the correct contact.
// TODO: I want to figure out not passing around the internal supporter ids for contacts.
const useSupporterAsContact = (
  supporter: SupporterDetailsData,
): GraphQLSupporterContact => {
  const { contactId } = useReadyForQueries();
  return {
    id: contactId ?? '',
    isPrimary: true,
    firstName: supporter.firstName,
    lastName: supporter.lastName,
    preferredName: supporter.preferredName,
    email: supporter.email,
    phone: supporter.phone,
  };
};

type SettingsTriggerProps = React.ComponentPropsWithoutRef<'button'> & {
  tooltip?: string;
  ref?: React.Ref<HTMLButtonElement>;
};

const SettingsTrigger = ({
  tooltip,
  ref,
  ...buttonProps
}: SettingsTriggerProps) => {
  const { t } = useTranslation('core');
  const label = t('actions.edit');
  return tooltip ? (
    <Tooltip>
      <TooltipContent
        sideOffset={-5}
        usePortal
        align="end"
        side="top"
        size="sm"
      >
        <p>{tooltip}</p>
      </TooltipContent>
      <TooltipTrigger>
        <LinkStyle iconBefore="lock-01" size="lg" noUnderline>
          <button type="button" ref={ref} {...buttonProps}>
            {label}
          </button>
        </LinkStyle>
      </TooltipTrigger>
    </Tooltip>
  ) : (
    <LinkStyle noUnderline size="lg">
      <button type="button" ref={ref} {...buttonProps}>
        {label}
      </button>
    </LinkStyle>
  );
};

const SettingsBlock = ({
  label,
  value,
}: {
  label?: string;
  value?: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col flex-1 gap-snug-xl">
      {label && (
        <span className="body-sm label-neutral-mild text-wrap-nowrap">
          {label}
        </span>
      )}
      <span className="body-sm label-neutral-amplified">{value}</span>
    </div>
  );
};

const ProfileBlock = ({ initials }: { initials?: string }) => {
  const { t } = useTranslation('my-settings');

  return (
    <div className="flex flex-1 pr-comfy-lg gap-comfy-sm items-center">
      <div className="AccountSettings__profile rounded-full flex rounded-full shrink-0 items-center justify-center">
        <SupporterIdentity variant="minimal" initials={initials} />
      </div>
      <span className="label-md label-neutral-mild">
        {t('my-settings:account.profile.description')}
      </span>
    </div>
  );
};

export const AccountSettingsSection: React.FC<AccountSettingsSectionProps> = ({
  title,
  children,
}) => {
  return (
    <LocalAlertsProvider>
      <div className="AccountSettings flex flex-col gap-comfy-xl max-w-full py-comfy-lg md:py-comfy-xl">
        {title && (
          <h3 className="label-neutral-amplified label-potent label-xs">
            {title}
          </h3>
        )}
        <LocalAlerts />
        <div className="flex gap-snug-xl items-center">{children}</div>
      </div>
    </LocalAlertsProvider>
  );
};

export const AccountSettingsContent: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="flex flex-1 flex-col gap-comfy-lg md:flex-row">
    {children}
  </div>
);

export const SettingDivider = () => (
  <div className="AccountSettings__divider" />
);

export const AccountSettingsInformation = ({
  supporter,
}: {
  supporter: SupporterDetailsData;
}) => {
  const { t } = useTranslation('my-settings');
  return (
    <AccountSettingsSection title={t('my-settings:account.info.title')}>
      <AccountSettingsContent>
        <SettingsBlock
          label={t('my-settings:account.info.nameLabel')}
          value={getSupporterFullName(supporter)}
        />
        <SettingsBlock
          label={t('my-settings:account.info.sponsorNumberLabel')}
          value={supporter.globalId}
        />
      </AccountSettingsContent>
      <SettingsTrigger
        disabled
        tooltip={t('my-settings:account.editTooltip')}
      />
    </AccountSettingsSection>
  );
};

export const AccountSettingsProfile = ({
  supporter,
}: {
  supporter: SupporterDetailsData;
}) => {
  const { t } = useTranslation('my-settings');
  const initials = getSupporterInitials(supporter);
  const contact = useSupporterAsContact(supporter);
  return (
    <AccountSettingsSection title={t('my-settings:account.profile.title')}>
      <AccountSettingsContent>
        <SettingsBlock
          label={t('my-settings:account.profile.preferredNameLabel')}
          value={supporter.preferredName}
        />
        <ProfileBlock initials={initials} />
      </AccountSettingsContent>
      <SettingsModalContact
        contact={contact}
        openTrigger={<SettingsTrigger />}
      />
    </AccountSettingsSection>
  );
};

export const AccountSettingsUsernamePassword = ({
  username,
}: {
  username: string;
}) => {
  const { t } = useTranslation('my-settings');
  return (
    <AccountSettingsSection title={t('my-settings:account.credentials.title')}>
      <AccountSettingsContent>
        <div className="flex flex-1">
          <SettingsBlock
            label={t('my-settings:account.credentials.usernameLabel')}
            value={username}
          />
          <SettingsModalUsername
            username={username}
            openTrigger={<SettingsTrigger />}
          />
        </div>
        <div className="flex flex-1">
          <SettingsBlock
            label={t('my-settings:account.credentials.passwordLabel')}
            value={SENSITIVE_MASK_CHAR.repeat(12)}
          />
          <SettingsModalPassword openTrigger={<SettingsTrigger />} />
        </div>
      </AccountSettingsContent>
    </AccountSettingsSection>
  );
};

export const AccountSettingsMailingAddress = ({
  addressDetails,
}: {
  addressDetails: GraphQLSupporterAddress;
}) => {
  const { t } = useTranslation('my-settings');

  return (
    <AccountSettingsSection title={t('my-settings:account.address.title')}>
      <AccountSettingsContent>
        <SettingsBlock
          value={
            <div className="flex flex-col gap-snug-lg">
              <span>{addressDetails?.street}</span>
              <span>
                {addressDetails?.city}, {addressDetails?.state}{' '}
                {addressDetails?.postalCode}
              </span>
              <span>{addressDetails?.country}</span>
            </div>
          }
        />
      </AccountSettingsContent>
      <SettingsModalMailing
        addressDetails={addressDetails}
        openTrigger={<SettingsTrigger />}
      />
    </AccountSettingsSection>
  );
};

export const AccountSettingsEmailPhone = ({
  supporter,
}: {
  supporter: SupporterDetailsData;
}) => {
  const { t } = useTranslation('my-settings');
  const contact = useSupporterAsContact(supporter);
  return (
    <AccountSettingsSection title={t('my-settings:account.contact.title')}>
      <AccountSettingsContent>
        <SettingsBlock
          label={t('my-settings:account.contact.emailLabel')}
          value={supporter.email}
        />
        <SettingsBlock
          label={t('my-settings:account.contact.phoneLabel')}
          value={supporter.phone}
        />
      </AccountSettingsContent>
      <SettingsModalContact
        contact={contact}
        openTrigger={<SettingsTrigger />}
      />
    </AccountSettingsSection>
  );
};
